import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";

import { safeString } from "./calendarInviteData.helper";

/**
 * How the thread shows a meeting invitation the lead sent us.
 *
 * - `"none"` — not an invitation; the bubble renders as usual.
 * - `"line"` — one status line. The invitation reached a **primary** mailbox, so the user already
 *   has it in their own inbox and their own calendar; repeating the card here is noise.
 * - `"card"` — the full invite card with "Add to my calendar". The invitation reached a
 *   **secondary** mailbox — an outreach address nobody reads — so this is the only place the lead's
 *   meeting is ever shown, and losing it would lose the meeting (PREC-3902).
 */
export type CalendarInviteDisplay = "none" | "line" | "card";

/** An invitation from the lead, as opposed to their client's answer to one of ours (PREC-3901). */
export const isCalendarInvite = (conversation: Conversation): boolean =>
	conversation.type === "mail_received" &&
	Boolean(conversation.hasCalendarInvite) &&
	Boolean(conversation.calendarInviteData) &&
	!conversation.calendarResponse;

/**
 * Decide from the **receiving mailbox**, never from who is looking: `email.type` is the mailbox's
 * own primary/secondary flag, the same axis the engagement notification preferences use.
 *
 * An unknown mailbox type is treated as secondary on purpose — collapsing is only ever right when
 * we positively know the mail also landed somewhere the user reads, and a missing `email` on the
 * conversation is not that.
 */
export const resolveCalendarInviteDisplay = (conversation: Conversation): CalendarInviteDisplay => {
	if (!isCalendarInvite(conversation)) {
		return "none";
	}

	return conversation.email?.type === "primary" ? "line" : "card";
};

/**
 * The event's title for the one-line marker and the lead-card preview: what the organiser called
 * the meeting, falling back to the mail's subject. `node-ical` hands some fields back as
 * `{ val, params }`, which the schema already unwraps for `eventSummary`.
 */
export const calendarInviteTitle = (conversation: Conversation): string =>
	(safeString(conversation.calendarInviteData?.eventSummary) || conversation.subject || "").trim();
